import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Grid, Typography } from '@mui/material'
import i18n from 'simple-react-i18n'
import { uniq } from 'lodash'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import { CardTitle } from '../../../../components/card/NewCard'
import Select from '../../../../components/forms/Select'
import Checkbox from '../../../../components/forms/Checkbox'
import Input from '../../../../components/forms/Input'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import { MANAGEMENT_MODE, MANAGEMENT_TYPE } from '../../../constants/RpqsConstant'

const getSkills = () => [
    { label: i18n.production, skill: 'productionSkill', convention: 'productionConvention' },
    { label: i18n.transport, skill: 'transportSkill', convention: 'transportConvention' },
    { label: i18n.protectionOfTheSamplingPoint, skill: 'protectionSkill', convention: 'protectionConvention' },
    { label: i18n.storage, skill: 'storageSkill', convention: 'storageConvention' },
    { label: i18n.job, skill: 'treatmentSkill', convention: 'treatmentConvention' },
    { label: i18n.distribution, skill: 'distributionSkill', convention: 'distributionConvention' },
]

const getAllSkills = value => Object.fromEntries(getSkills().map(({ skill }) => [skill, value]))
const getAllConventions = value => Object.fromEntries(getSkills().map(({ convention }) => [convention, value]))

// Oui / Non exclusifs + date d'approbation, saisissable uniquement sur Oui.
const YesNoDateField = ({ label, value, date, onChange, disabled }) => (
    <Grid container size={12} alignItems='center' spacing={1}>
        <Grid size={12}>
            <Typography>{label}</Typography>
        </Grid>
        <Grid size={2}>
            <Checkbox
                label={i18n.yes}
                checked={value === true}
                onChange={checked => onChange(checked || undefined, checked ? date : undefined)}
                disabled={disabled}
            />
        </Grid>
        <Grid size={4}>
            <SimpleDatePicker
                label={i18n.rpqsApprovalDate}
                value={date}
                onChange={newDate => onChange(value, newDate)}
                disabled={disabled || value !== true}
            />
        </Grid>
        <Grid size={2}>
            <Checkbox
                label={i18n.no}
                checked={value === false}
                onChange={checked => onChange(checked ? false : undefined, undefined)}
                disabled={disabled}
            />
        </Grid>
    </Grid>
)

YesNoDateField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.bool,
    date: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
}

// Règles (spec « Compétences exercées ») :
// - Unique et Multiple sont exclusifs.
// - Unique : le type de gestion devient saisissable. Régie passe toutes les compétences en Régie, sans convention.
//   Délégation passe toutes les compétences en Délégation et rend le délégataire saisissable (noms des
//   prestataires des conventions) : le choisir alimente toutes les conventions d'un coup.
// - Multiple : type de gestion et délégataire non saisissables, chaque compétence est renseignée individuellement.
const ManagementUnitSkillsPanel = ({
    managementUnit = {},
    onChange = () => {},
    conventions = [],
    skillsList = [],
    readMode = true,
}) => {
    const { managementMode, managementType } = managementUnit
    const isSingle = managementMode === MANAGEMENT_MODE.SINGLE

    const typeOptions = useMemo(() => skillsList.filter(s => [MANAGEMENT_TYPE.REGIE, MANAGEMENT_TYPE.DELEGATION].includes(s.code)), [skillsList])
    const delegateOptions = useMemo(() => uniq(conventions.map(c => c.providerName).filter(name => !!name)).map(name => ({ id: name, name })), [conventions])
    const conventionOptions = useMemo(() => conventions.map(c => ({
        id: c.conventionId,
        name: c.providerName ? `${c.convention} - ${c.providerName}` : c.convention,
    })), [conventions])

    const changeMode = (mode, checked) => {
        const newMode = checked ? mode : undefined
        onChange(newMode === MANAGEMENT_MODE.MULTIPLE ? { managementMode: newMode, managementType: undefined, delegate: undefined } : { managementMode: newMode })
    }

    const changeType = type => {
        if (type === MANAGEMENT_TYPE.REGIE) {
            onChange({ managementType: type, delegate: undefined, ...getAllSkills(MANAGEMENT_TYPE.REGIE), ...getAllConventions(undefined) })
        } else if (type === MANAGEMENT_TYPE.DELEGATION) {
            onChange({ managementType: type, ...getAllSkills(MANAGEMENT_TYPE.DELEGATION) })
        } else {
            onChange({ managementType: type, delegate: undefined })
        }
    }

    const changeDelegate = delegate => {
        const conventionId = conventions.find(c => c.providerName === delegate)?.conventionId
        onChange({ delegate, ...getAllConventions(conventionId) })
    }

    const changeSkill = (skill, convention, value) => onChange(value === MANAGEMENT_TYPE.REGIE ? { [skill]: value, [convention]: undefined } : { [skill]: value })

    return (
        <Card>
            <CardTitle title={i18n.skillsApplied} icon={<MenuBookIcon fontSize='large'/>}/>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={3}>
                        <Typography>{i18n.managementMode}</Typography>
                        <Checkbox
                            label={i18n.managementModeSingle}
                            checked={isSingle}
                            onChange={checked => changeMode(MANAGEMENT_MODE.SINGLE, checked)}
                            disabled={readMode}
                        />
                        <Checkbox
                            label={i18n.managementModeMultiple}
                            checked={managementMode === MANAGEMENT_MODE.MULTIPLE}
                            onChange={checked => changeMode(MANAGEMENT_MODE.MULTIPLE, checked)}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.managementType}
                            options={typeOptions}
                            value={managementType}
                            onChange={changeType}
                            disabled={readMode || !isSingle}
                        />
                    </Grid>
                    <Grid size={5}>
                        <Select
                            label={i18n.delegatee}
                            options={delegateOptions}
                            value={managementUnit.delegate}
                            keyValue='id'
                            onChange={changeDelegate}
                            disabled={readMode || !isSingle || managementType !== MANAGEMENT_TYPE.DELEGATION}
                        />
                    </Grid>
                    {getSkills().map(({ label, skill, convention }) => (
                        <Grid container size={6} spacing={1} key={skill}>
                            <Grid size={12}>
                                <Typography fontWeight='bold'>{label}</Typography>
                            </Grid>
                            <Grid size={6}>
                                <Select
                                    label={i18n.managementMode}
                                    options={skillsList}
                                    value={managementUnit[skill]}
                                    onChange={value => changeSkill(skill, convention, value)}
                                    disabled={readMode || isSingle}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Select
                                    label={i18n.agreement}
                                    options={conventionOptions}
                                    value={managementUnit[convention]}
                                    keyValue='id'
                                    onChange={value => onChange({ [convention]: value })}
                                    disabled={readMode || isSingle || managementUnit[skill] === MANAGEMENT_TYPE.REGIE}
                                />
                            </Grid>
                        </Grid>
                    ))}
                    <Grid size={9}>
                        <Input
                            title={i18n.oldOwner}
                            value={managementUnit.previousOwner}
                            onChange={previousOwner => onChange({ previousOwner })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={3}>
                        <SimpleDatePicker
                            label={i18n.transferDate}
                            value={managementUnit.transferDate}
                            onChange={transferDate => onChange({ transferDate })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Checkbox
                            label={i18n.IsThisAMunucipalityWithACCSPL}
                            checked={managementUnit.ccspl}
                            onChange={ccspl => onChange({ ccspl })}
                            disabled={readMode}
                        />
                    </Grid>
                    <YesNoDateField
                        label={i18n.distributionSchemeExists}
                        value={managementUnit.distributionScheme}
                        date={managementUnit.distributionSchemeDate}
                        onChange={(distributionScheme, distributionSchemeDate) => onChange({ distributionScheme, distributionSchemeDate })}
                        disabled={readMode}
                    />
                    <YesNoDateField
                        label={i18n.serviceRegulationExists}
                        value={managementUnit.serviceRegulation}
                        date={managementUnit.serviceRegulationDate}
                        onChange={(serviceRegulation, serviceRegulationDate) => onChange({ serviceRegulation, serviceRegulationDate })}
                        disabled={readMode}
                    />
                    <YesNoDateField
                        label={i18n.masterPlanExists}
                        value={managementUnit.masterPlan}
                        date={managementUnit.masterPlanDate}
                        onChange={(masterPlan, masterPlanDate) => onChange({ masterPlan, masterPlanDate })}
                        disabled={readMode}
                    />
                </Grid>
            </CardContent>
        </Card>
    )
}

ManagementUnitSkillsPanel.propTypes = {
    managementUnit: PropTypes.shape({}),
    onChange: PropTypes.func,
    conventions: PropTypes.arrayOf(PropTypes.shape({
        conventionId: PropTypes.number,
        convention: PropTypes.string,
        providerName: PropTypes.string,
    })),
    skillsList: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.number, name: PropTypes.string })),
    readMode: PropTypes.bool,
}

export default ManagementUnitSkillsPanel
